/* eslint-disable @typescript-eslint/no-explicit-any */

import { LoggerApi, LoggerApiHandler, LoggerInternalApi, LoggerLevel, LoggerMetadata } from './logger.types';
import { getConsolePrinters, logEntryDefault, makeLoggerInternalApi } from './logger.utils';

const LOGGER_LEVEL_DEFAULT: LoggerLevel = 'INFO';
const LOGGER_ENTRY_HANDLER_DEFAULT: LoggerApiHandler = logEntryDefault;
const LOGGER_PRINTERS_DEFAULT = getConsolePrinters(console, true);
const LOGGER_INTERNAL_API_DEFAULT = makeLoggerInternalApi(
  LOGGER_LEVEL_DEFAULT,
  LOGGER_ENTRY_HANDLER_DEFAULT,
  LOGGER_PRINTERS_DEFAULT,
);

export class Logger implements LoggerApi {
  static get level(): LoggerLevel {
    return this._level;
  }

  static set level(value: LoggerLevel) {
    this.configure({ level: value });
  }

  static get handler(): LoggerApiHandler {
    return this._handler;
  }

  static set handler(value: LoggerApiHandler) {
    this.configure({ handler: value });
  }

  protected static _level = LOGGER_LEVEL_DEFAULT;
  protected static _handler = LOGGER_ENTRY_HANDLER_DEFAULT;
  protected static printers = LOGGER_PRINTERS_DEFAULT;
  protected static internalApi = LOGGER_INTERNAL_API_DEFAULT;

  static configure(options: {
    consoleApi?: Console;
    consoleBinding?: boolean;
    handler?: LoggerApiHandler;
    level?: string | LoggerLevel;
  }): void {
    if (options.level) {
      this._level = options.level as LoggerLevel;
    }

    if (options.handler) {
      this._handler = options.handler;
    }

    if (options.consoleApi) {
      this.printers = getConsolePrinters(options.consoleApi, options.consoleBinding);
    }

    if (options.level || options.handler || options.consoleApi) {
      this.internalApi = makeLoggerInternalApi(this.level, this.handler, this.printers);
    }
  }

  get internalApi(): LoggerInternalApi {
    return this.ctor.internalApi;
  }

  readonly metadata: LoggerMetadata;

  protected readonly ctor: typeof Logger;

  constructor(contextOrMetadata: string | LoggerMetadata = {}) {
    this.ctor = new.target;
    this.metadata = typeof contextOrMetadata === 'string' ? { context: contextOrMetadata } : contextOrMetadata;
  }

  log(message: any, ...optionalParams: any[]): void {
    this.internalApi.info(this.metadata, message, optionalParams);
  }

  fatal(message: any, ...optionalParams: any[]): void {
    this.internalApi.fatal(this.metadata, message, optionalParams);
  }

  error(message: any, ...optionalParams: any[]): void {
    this.internalApi.error(this.metadata, message, optionalParams);
  }

  warn(message: any, ...optionalParams: any[]): void {
    this.internalApi.warn(this.metadata, message, optionalParams);
  }

  info(message: any, ...optionalParams: any[]): void {
    this.internalApi.info(this.metadata, message, optionalParams);
  }

  debug(message: any, ...optionalParams: any[]): void {
    this.internalApi.debug(this.metadata, message, optionalParams);
  }
}
